import { WORKSPACE_PROVIDERS } from '@/containers/integrations/workspace-connection-card.component';
import { BRAND } from '@/lib/brand';

/**
 * The words for a connect that came back with an error code: the toast the
 * Integrations page shows for it, and the line Clu's connection card shows
 * when its sign-in window reports the same code.
 */

// Backend OAuth error codes -> a human sentence; unknown codes de-snake as-is.
export const OAUTH_ERROR_LABELS: Record<string, string> = {
  not_configured: 'the integration is not configured yet',
  expired_retry: 'the session expired — please try connecting again',
  access_denied: 'access was denied',
  invalid_state: 'the security check failed — please try again',
  scope_denied:
    'Google Drive access wasn’t granted — tick it on the consent screen',
  calendar_scope_denied:
    'Google Calendar read access was not granted. Select it on the consent screen.',
  not_allowed:
    'only the member who connected it or an org admin can replace it',
  session_mismatch:
    'it was started from a different account or organization — connect again from here',
  meetings_scope_denied: 'access to your team’s meetings wasn’t granted',
  calls_scope_denied: 'access to your company’s Gong calls wasn’t granted',
  denied: 'access was denied',
  not_enabled: 'it isn’t available for your workspace yet',
  start_in_cleanlist: `start the connection from ${BRAND.name}’s Integrations page, not from the Pipedrive Marketplace`,
};
export const humanizeOAuthError = (code: string): string =>
  OAUTH_ERROR_LABELS[code] ?? code.replace(/_/g, ' ');
// Attio's own refusals; its `scope_denied` isn't the Google one above.
export const ATTIO_ERROR_LABELS: Record<string, string> = {
  scope_denied: `not every permission ${BRAND.name} asks for was granted — approve them all on Attio’s screen`,
  workspace_token_required: 'a workspace admin has to connect Attio',
  not_in_plan: 'Attio isn’t included in your plan',
  appsumo_plan: 'Attio isn’t available on AppSumo plans',
  plan_check_failed: 'your plan couldn’t be checked. Try again.',
  attio_unavailable: 'Attio isn’t available right now. Try again.',
  authorize_failed: 'the connection couldn’t start. Try again.',
};

/** What Slack's callback `slack_error` codes mean. */
export const SLACK_ERROR_COPY: Record<string, string> = {
  access_denied: 'Slack authorization was cancelled.',
  missing_code: 'Slack did not return an authorization code. Try again.',
  invalid_state: 'The connection link expired. Try again.',
  token_exchange: 'Slack rejected the connection. Try again.',
  enterprise_grid:
    'Org-wide Enterprise Grid installs are not supported yet. Install into a single workspace.',
  workspace_conflict: `That Slack workspace is already connected to another ${BRAND.name} organization.`,
};

const CRM_NAMES: Record<string, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  pipedrive: 'Pipedrive',
  lemlist: 'Lemlist',
  outreach: 'Outreach',
};

/** The Integrations page's toast for `kind`'s connect failing with `code`. */
export function connectFailedMessage(kind: string, code: string): string {
  if (kind === 'slack')
    return SLACK_ERROR_COPY[code] ?? `Slack connection failed (${code}).`;
  if (kind === 'attio')
    return `Attio connection failed: ${ATTIO_ERROR_LABELS[code] ?? humanizeOAuthError(code)}`;
  const name = Object.prototype.hasOwnProperty.call(WORKSPACE_PROVIDERS, kind)
    ? WORKSPACE_PROVIDERS[kind as keyof typeof WORKSPACE_PROVIDERS].vendor
    : (CRM_NAMES[kind] ?? kind);
  return `${name} connection failed: ${humanizeOAuthError(code)}`;
}
